import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import { MoreHorizIcon } from '@/components/icons';
import { keyframes } from '@mui/system';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { ChatMessage, useChatBot, useGetConfig } from './ChatBot';
import { MessageFeedback } from './MessageFeedback';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetBrandColors } from './useWidgetBrandColors';
import { markdownHeadingComponents } from './markdownHeadingLevels';
import { MarkdownExternalLink } from '@/lib/markdown-external-link';
import { BotMessageHeading } from './_parts/BotMessageHeading';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

/** Find the user message immediately before a given bot message in the history */
function getPrecedingUserMessage(botMsg: ChatMessage, history: ChatMessage[]): { messageId?: string } {
  const idx = history.findIndex(m => m.timestamp === botMsg.timestamp);
  if (idx <= 0) return {};
  // Walk backwards to find the closest user message
  for (let i = idx - 1; i >= 0; i--) {
    if (history[i].role === 'user') {
      return {
        messageId: history[i].messageId,
      };
    }
  }
  return {};
}

export const Message = (props: { msg: ChatMessage; history: ChatMessage[] }) => {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const { primaryOnPaper } = useWidgetBrandColors();
  const config = useGetConfig();
  const { updateFeedback, threadId, state } = useChatBot();
  const msg = props.msg;
  const pending = !msg.text;
  const shouldScaleTypography =
    ((theme as { widgetTypographyScale?: number }).widgetTypographyScale ?? 1) > 1;

  // Animation for the pending icon
  const pulse = keyframes`
    0% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
    100% { opacity: 0.3; transform: scale(1); }
  `;

  const isBotMessage = msg.role === 'bot';
  const showFeedback = !pending && isBotMessage;

  return (
    <Box
      display="flex"
      justifyContent={isBotMessage ? 'flex-start' : 'flex-end'}
      px={1}
      py={0.5}
      sx={{ width: '100%' }}
    >
      <Box sx={{ maxWidth: '80%' }}>
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 4,
          ...(isBotMessage
            ? { borderTopLeftRadius: 0 }
            : { borderTopRightRadius: 0 }),
          color: isBotMessage ? 'text.primary' : 'primary.contrastText',
          bgcolor:
            isBotMessage ? 'common.white' : theme.palette.primary.main,
        }}
      >
        {!pending && msg.role === 'bot' && (
          <BotMessageHeading agentName={config.agentName} />
        )}

        {pending && (
          <Box
            component="span"
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            position="relative"
          >
            <MoreHorizIcon
              aria-hidden
              sx={{
                animation: `${pulse} 1.2s infinite cubic-bezier(0.4,0,0.2,1)`,
              }}
            />
            <Box
              component="span"
              lang={locale}
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                p: 0,
                m: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {t('feedback.assistantReplying')}
            </Box>
          </Box>
        )}

        {!pending && (
          <Typography
            variant="subtitle2"
            component="div"
            fontWeight={300}
            fontSize={shouldScaleTypography ? scaledFontSize(theme, 14) : undefined}
            sx={{ lineHeight: 1.6 }}
          >
            <Box
              dir="auto"
              sx={{
                overflow: 'auto',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                '& a': {
                  color: msg.role === 'user' ? 'primary.contrastText' : primaryOnPaper,
                  textDecoration: 'underline',
                },
                '& ul, & ol': {
                  paddingInlineStart: '20px',
                  mt: 0.5,
                  mb: 1.5,
                  listStylePosition: 'outside',
                  '&:last-child': { mb: 0 },
                },
                '& ul': { listStyleType: 'disc' },
                '& ol': { listStyleType: 'decimal' },
                '& li': { mb: 0.25, display: 'list-item' },
                '& code': {
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  bgcolor: 'grey.200',
                  px: 0.5,
                  borderRadius: 0.5,
                },
                '& pre': {
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  bgcolor: 'grey.200',
                  p: 1,
                  borderRadius: 1,
                  overflowX: 'auto',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 0.5,
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 0.75,
                  textAlign: 'start',
                  verticalAlign: 'top',
                },
                '& th': {
                  bgcolor: 'grey.200',
                  fontWeight: 600,
                },
                '& blockquote': {
                  borderInlineStart: '3px solid',
                  borderColor: 'grey.300',
                  paddingInlineStart: '8px',
                  marginInlineStart: 0,
                  color: 'text.secondary',
                },
                '& hr': { borderColor: 'divider', my: 1 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 1,
                  mb: 1.5,
                  '&:first-of-type': { mt: 0 },
                  '&:last-child': { mb: 0 },
                },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ...markdownHeadingComponents,
                  a: props => <MarkdownExternalLink {...props} />,
                }}
              >
                {msg.text}
              </ReactMarkdown>
            </Box>
          </Typography>
        )}

        {!pending && msg.timestamp && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} gap={0.25}>
            <Typography
              component="p"
              variant="subtitle1"
              fontSize={scaledFontSize(theme, 10)}
              fontWeight={100}
              color={msg.role === 'bot' ? 'textDisabled' : 'primary.contrastText'}
            >
              {new Date(msg.timestamp).toLocaleTimeString()}
            </Typography>
          </Box>
        )}

      </Paper>

      {showFeedback && (() => {
        const precedingUser = getPrecedingUserMessage(msg, props.history);
        return (
          <MessageFeedback
            timestamp={msg.timestamp}
            feedback={msg.feedback}
            threadId={threadId}
            botMessageText={msg.text}
            messageId={precedingUser.messageId}
            isStreaming={state === 'pending'}
            onFeedback={(rating, comment) => updateFeedback(msg.timestamp, rating, comment)}
          />
        );
      })()}
      </Box>
    </Box>
  );
};

export default Message;
